import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';

const WHITELIST: ReadonlySet<string> = new Set([
  'barallat@gmail.com',
  'ricardobrocop@gmail.com',
  'mateogonsilva@gmail.com',
  'pablorturco@gmail.com',
  'projo2007@gmail.com',
]);

type AuthState = {
  user: User | null;
  loading: boolean;
  error: string | null;
  allowed: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const handleSignIn = useCallback(async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Login fallido';
      setError(msg);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut(auth);
  }, []);

  const allowed = useMemo(() => {
    const email = user?.email?.toLowerCase() ?? '';
    return !!user && user.emailVerified && WHITELIST.has(email);
  }, [user]);

  const value: AuthState = {
    user,
    loading,
    error,
    allowed,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}
